import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Menu, Moon, Settings, Sun, X } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Button } from '../ui/Button';
import { Logo } from './Logo';
import { Sidebar, usePageTitle } from './Sidebar';

export function Layout({ children }: { children: React.ReactNode }) {
  const { section, label } = usePageTitle();
  const { tenants, tenantId, workspaces, workspaceId, setTenant, setWorkspace, dark, toggleTheme, logout, name, ready } = useApp();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const tenant = tenants.find((t) => t.id === tenantId);
  const workspace = workspaces.find((w) => w.id === workspaceId);

  return (
    <div className="flex h-full min-h-screen bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <div className="hidden lg:block">
        <Sidebar />
      </div>
      {open ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button className="absolute inset-0 bg-black/40" aria-label="Close navigation" onClick={() => setOpen(false)} />
          <div className="relative z-10 h-full w-72">
            <Sidebar onNavigate={() => setOpen(false)} />
          </div>
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-gray-200 bg-white px-4 dark:border-gray-800 dark:bg-gray-900 lg:px-8">
          <button className="lg:hidden" aria-label="Open navigation" onClick={() => setOpen(true)}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="lg:hidden">
            <Logo size={28} word={false} />
          </div>
          <div className="hidden min-w-0 flex-col lg:flex">
            <span className="text-[0.7rem] uppercase tracking-[0.16em] text-gray-400">{section}</span>
            <h1 className="truncate text-lg font-semibold leading-tight">{label}</h1>
          </div>
          <div className="ml-auto flex min-w-0 items-center gap-2">
            {tenants.length > 1 ? (
              <select
                aria-label="Tenant"
                className="h-8 max-w-[10rem] rounded-md border border-gray-300 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                value={tenantId ?? ''}
                onChange={(e) => setTenant(e.target.value)}
              >
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            ) : tenant ? (
              <span className="hidden text-sm sm:inline">{tenant.name}</span>
            ) : (
              <Link to="/tenants" className="text-sm text-blue-600">
                New tenant
              </Link>
            )}
            {tenantId ? (
              workspaces.length ? (
                <select
                  aria-label="Workspace"
                  className="h-8 max-w-[10rem] rounded-md border border-gray-300 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                  value={workspaceId ?? ''}
                  onChange={(e) => setWorkspace(e.target.value)}
                >
                  {workspaces.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              ) : (
                <Link to="/workspaces" className="text-sm text-blue-600">
                  New workspace
                </Link>
              )
            ) : null}
            <Link to="/settings" aria-label="Settings" className="rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-800">
              <Settings className="h-4 w-4" />
            </Link>
            <button type="button" aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'} onClick={toggleTheme} className="rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-800">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <span className="hidden max-w-[12rem] truncate text-xs text-gray-500 lg:inline">{name}</span>
            <button type="button" aria-label="Log out" onClick={logout} className="rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-800">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>
        <main key={location.pathname} data-scroll-container className="flex-1 overflow-auto px-4 py-6 lg:px-8 lg:py-8">
          {ready ? <div className="mx-auto w-full max-w-[1440px]">{children}</div> : <div className="py-20 text-center text-sm text-gray-500">Loading</div>}
        </main>
      </div>
    </div>
  );
}

export function BusyButton(props: React.ComponentProps<typeof Button> & { busy?: boolean }) {
  const { busy, children, disabled, ...rest } = props;
  return (
    <Button disabled={disabled || busy} {...rest}>
      {children}
    </Button>
  );
}
